import React, { useRef, useState } from 'react';
import { Table, Button, Select, Tag, Space, Typography, Alert, message } from 'antd';
import PageShell from '../components/page/PageShell';
import PageHeader from '../components/page/PageHeader';
import PageState from '../components/page/PageState';
import { ReloadOutlined, DownloadOutlined } from '@ant-design/icons';
import { useApp } from '../context/AppContext';
import { Role } from '../constants/roles';
import { useSupportApi } from '../hooks/useSupportApi';
import { useResource } from '../hooks/useResource';
import AiAgreementIndicator from '../components/AiAgreementIndicator';
import { SQA_TOOL_NAMES, humanizeEnum, formatDdMmYyyyTime } from '../utils/supportApi';

const { Title, Text, Paragraph } = Typography;

/** Save a starter template the server returned (`content` is real platform-shipped text). */
function downloadTemplate(t) {
    const url = URL.createObjectURL(new Blob([t.content], { type: 'text/plain;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = t.fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

/**
 * Software QA results for the open project (Sheet-2 toolkit #10, Sub-workstream C of
 * .claude/design/backoffice-ticketing-pathkey-sqa.md). An Advisor / Lead Advisor downloads a
 * starter template, runs it in their own CI, then uploads the ALREADY-EXECUTED result file —
 * there is no hosted runner (the backend says so in sqa.service.ts), so this page does not
 * offer to "run" anything.
 *
 * Commandment IV: the AI summary is a DRAFT and shows the "AI drafted — review" chip until a
 * human records a review (`reviewedBy`); the review button is the human-approval event. The
 * backend's `toResultDto` returns a numeric `aiConfidence` (0..1 run-agreement, or null) plus
 * `confidenceProvenance` (runs, valid samples, reasoning refs, unavailable reason); both are
 * passed to the shared AiAgreementIndicator. When no number exists the indicator states the
 * reason instead of inventing a score.
 *
 * Persona gate is UX only: `assertProjectAccess` in sqa.service.ts enforces tenant + project
 * membership + lead_advisor/advisor role server-side on every route except the template list.
 */
const SqaResultsPage = () => {
    const { user, activeProject } = useApp();
    const api = useSupportApi();
    const projectId = activeProject ? activeProject.id : null;
    const allowed = user?.role === Role.LEAD_ADVISOR || user?.role === Role.ADVISOR;

    const templates = useResource(() => api.listSqaTemplates(), [api], { enabled: allowed });
    const results = useResource(() => api.listSqaResults(projectId), [api, projectId], { enabled: allowed && !!projectId });

    const [toolName, setToolName] = useState(undefined);
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [reviewingId, setReviewingId] = useState(null);
    const fileRef = useRef(null);

    if (!allowed) return <PageState status="empty" message="SQA results are available to Lead Advisors and Advisors." />;
    if (!projectId) return <PageState status="empty" message="Open a project to see its SQA results." />;

    const upload = async () => {
        setUploading(true);
        try {
            await api.uploadSqaResult(projectId, toolName, file);
            message.success('Result uploaded. The AI summary is a draft until you review it.');
            setFile(null);
            setToolName(undefined);
            if (fileRef.current) fileRef.current.value = '';
            results.reload();
        } catch (err) {
            message.error(err.message);
        } finally {
            setUploading(false);
        }
    };

    const review = async (r) => {
        setReviewingId(r.id);
        try {
            await api.reviewSqaResult(projectId, r.id);
            message.success('Review recorded.');
            results.reload();
        } catch (err) {
            message.error(err.message);
        } finally {
            setReviewingId(null);
        }
    };

    const columns = [
        { title: 'Tool', dataIndex: 'toolName', render: (v) => humanizeEnum(String(v).replace(/^sqa_/, '')) },
        { title: 'Uploaded', dataIndex: 'createdAt', render: (v) => formatDdMmYyyyTime(v) },
        {
            title: 'Tests',
            render: (_, r) => (
                <Space direction="vertical" size={0}>
                    <Text>{r.totalCount} total</Text>
                    <Text type="success">{r.passedCount} passed</Text>
                    <Text type={r.failedCount > 0 ? 'danger' : 'secondary'}>{r.failedCount} failed</Text>
                </Space>
            ),
        },
        {
            title: 'AI summary',
            render: (_, r) => {
                if (!r.aiSummaryJson) return <Text type="secondary">No AI summary was produced for this upload.</Text>;
                const s = r.aiSummaryJson;
                return (
                    <div>
                        <Space size={4} wrap>
                            {!r.reviewedBy && <Tag color="gold">AI drafted - review</Tag>}
                            {s.severity && <Tag>Severity: {humanizeEnum(String(s.severity))}</Tag>}
                        </Space>
                        <AiAgreementIndicator
                            approved={!!r.reviewedBy}
                            confidence={typeof r.aiConfidence === 'number' ? r.aiConfidence : r.confidenceProvenance?.confidence}
                            confidenceMethod={r.confidenceProvenance?.confidenceMethod}
                            runs={r.confidenceProvenance?.confidenceRuns}
                            validSamples={r.confidenceProvenance?.validSamples}
                            reasoningRefs={r.confidenceProvenance?.reasoningRefs}
                            unavailableReason={r.confidenceProvenance?.confidenceUnavailableReason}
                        />
                        <Paragraph style={{ margin: '6px 0 0' }}>{String(s.summary ?? '')}</Paragraph>
                        {Array.isArray(s.recommended_next_steps) && s.recommended_next_steps.length > 0 && (
                            <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                                {s.recommended_next_steps.map((step, i) => (
                                    <li key={i}>{String(step)}</li>
                                ))}
                            </ul>
                        )}
                    </div>
                );
            },
        },
        {
            title: 'Review',
            render: (_, r) =>
                r.reviewedBy ? (
                    <Tag color="green">Reviewed {r.reviewedAt ? formatDdMmYyyyTime(r.reviewedAt) : ''}</Tag>
                ) : (
                    <Button size="small" loading={reviewingId === r.id} onClick={() => review(r)}>
                        Mark reviewed
                    </Button>
                ),
        },
    ];

    const renderResults = () => {
        if (results.status === 'loading' || results.status === 'idle') return <PageState status="loading" />;
        if (results.status === 'error') {
            return (
                <Alert
                    type="error"
                    showIcon
                    message="Could not load SQA results"
                    description={results.error.message}
                    action={<Button onClick={results.reload}>Retry</Button>}
                />
            );
        }
        if (results.data.length === 0) {
            return <PageState status="empty" message="No SQA results uploaded for this project yet. Run a test template in your own CI, then upload its result file above." />;
        }
        return <Table rowKey={(r) => r.id} dataSource={results.data} columns={columns} pagination={{ pageSize: 10 }} />;
    };

    const renderTemplates = () => {
        if (templates.status === 'loading' || templates.status === 'idle') return <PageState status="loading" />;
        if (templates.status === 'error') {
            return <Alert type="error" showIcon message="Could not load test templates" description={templates.error.message} />;
        }
        if (templates.data.length === 0) return <Text type="secondary">No starter templates are available.</Text>;
        return templates.data.map((t) => (
            <div key={t.id} style={{ marginBottom: 10 }}>
                <Space wrap>
                    <Text strong>{t.title}</Text>
                    <Tag>{humanizeEnum(t.projectType)}</Tag>
                    <Button size="small" icon={<DownloadOutlined />} onClick={() => downloadTemplate(t)}>
                        Download {t.fileName}
                    </Button>
                </Space>
                <div><Text type="secondary">{t.description}</Text></div>
            </div>
        ));
    };

    return (
        <PageShell>
            <PageHeader
                title="Software QA results"
                subtitle="Upload the result file from a test run you executed yourself. PolicyLens parses the counts and drafts a summary; nothing is run here."
            />

            <Title level={5} style={{ marginTop: 20 }}>Starter test templates</Title>
            {renderTemplates()}

            <Title level={5} style={{ marginTop: 20 }}>Upload a result</Title>
            <Space wrap>
                <Select
                    aria-label="Test tool"
                    style={{ width: 200 }}
                    placeholder="Test tool"
                    value={toolName}
                    onChange={setToolName}
                    options={SQA_TOOL_NAMES.map((n) => ({ value: n, label: humanizeEnum(n.replace(/^sqa_/, '')) }))}
                />
                <input ref={fileRef} type="file" aria-label="Result file" onChange={(e) => setFile((e.target.files && e.target.files[0]) || null)} />
                <Button type="primary" loading={uploading} disabled={!toolName || !file} onClick={upload}>
                    Upload result
                </Button>
                <Button icon={<ReloadOutlined />} onClick={results.reload}>Refresh</Button>
            </Space>

            <Title level={5} style={{ marginTop: 20 }}>Uploaded results</Title>
            {renderResults()}
        </PageShell>
    );
};

export default SqaResultsPage;
